import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  getCompanyName,
  HStack,
  VStack,
} from "../../../components/utils";
import { Input, Label } from "../../../components/shared/InputField";
import { Select } from "../../../components/shared/Select";
import { MultiSelectV2 } from "../../../components/shared/MultiSelectV2";
import { ReportType } from "../utils/report";
import { OptionTypes } from "../../../components/shared/MultiSelect";
import { useEsopPlans, useSubsidiaryCompanyNames } from "../../../queries";
import { useGetScheduledReports } from "../../../queries/scheduledReports";
import { PlanState } from "../../../types/EsopPlan";
import { generateGrantReport } from "../../../api/Esop";
import { ReportDto } from "../../../types/Grant";
import {
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../../utils/DownloadFile";
import { useGetOptionBuyback } from "../../../queries/optionBuyback";
import { EventType } from "../../../types/OptionBuyback";
import {
  GrantState,
  HissaReportType,
  ScheduleParams,
} from "../../../types/common";
import ScheduleSection from "./ScheduleSection";
import { useGetAssociatedUsers } from "../../../queries/usersRoles";
import { SubsidiaryCompanyDropDown } from "../../../types/EsopSubsidiaryCompany";
import DateFormatSelect from "./DateFormatSelect";
import {
  DateFormatKey,
  DEFAULT_DATE_FORMAT_KEY,
} from "../../../utils/dateFormatCatalog";
import { useReportPiiMaskDefault } from "../../../queries/siteSettings";

const PoolStatementDialog = ({
  onClose = () => {},
  data,
}: Readonly<{
  onClose: () => void;
  data: any;
}>) => {
  const { data: _plans } = useEsopPlans();
  const plans = _plans || [];
  const planOptions: OptionTypes[] = [];
  const ids: string[] = [];
  const {
    maskPiiData: shouldPiiBeMasked,
    setMaskPiiData: setShouldPiiBeMasked,
  } = useReportPiiMaskDefault();
  const [dateFormat, setDateFormat] = useState<DateFormatKey>(
    DEFAULT_DATE_FORMAT_KEY
  );
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedDateType, setSelectedDateType] = useState("");
  const dateTypes = [
    "As of Date",
    "Current Financial Year",
    "Previous Financial Year",
    "Custom Interval",
  ];
  const [subsidiaryList, setSubsidiaryList] = useState<
    SubsidiaryCompanyDropDown[]
  >([]);
  const [subsidiaryNames, setSubsidiaryNames] = useState<OptionTypes[]>([]);
  const { data: subsidiaryCompanyNamesList } = useSubsidiaryCompanyNames();

  useEffect(() => {
    if (subsidiaryCompanyNamesList) {
      setSubsidiaryList(subsidiaryCompanyNamesList);
      setSubsidiaryNames(
        subsidiaryCompanyNamesList.map((s) => ({ value: s.name }))
      );
    }
  }, [subsidiaryCompanyNamesList]);
  const [scheduleParams, setScheduleParams] = useState<
    ScheduleParams | undefined
  >(undefined);
  const { data: associatedUsers } = useGetAssociatedUsers();
  const { data: scheduledReports = [], refetch: refetchScheduledReports } =
    useGetScheduledReports();
  const existingSchedule =
    data?.reportType &&
    scheduledReports.find(
      (s) => s.reportType === data.reportType && s.isActive
    );

  plans.forEach((plan) => {
    planOptions.push({ value: plan.planName });
    ids.push(plan.esopPlanId);
  });

  const grantStateLabels: { [key: string]: string } = Object.fromEntries(
    Object.values(GrantState).map((v) => [v.replace(/_/g, " "), v])
  );
  const getGrantStateLabel = (enumValue: string) => {
    for (const [key, value] of Object.entries(grantStateLabels)) {
      if (value === enumValue) return key;
    }
    return enumValue;
  };
  const grantStateOptions = Object.keys(grantStateLabels).map((label) => ({
    value: label,
  }));

  const initialValues: ReportType & { subsidiaryCompanyIds?: string[] } = {
    planIds: [],
    startDate: undefined,
    endDate: undefined,
    subsidiaryCompanyIds: [],
    grantStates: [GrantState.GRANTED, GrantState.OFFERED],
  };
  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      values.maskPiiData = shouldPiiBeMasked;
      values.dateFormat = dateFormat;
      const payload =
        scheduleParams && scheduleParams.recipients.length > 0
          ? { ...values, scheduleParams }
          : values;
      data
        ?.onSubmitFunction(payload)
        .then((res: any) => {
          if (scheduleParams && scheduleParams.recipients.length > 0) {
            refetchScheduledReports();
          }
          toast(
            scheduleParams && scheduleParams.recipients.length > 0
              ? "Report scheduled successfully"
              : "Please wait while your report is being generated",
            { type: "info", autoClose: 2000 }
          );
          onClose();
        })
        .catch((err: any) => {
          toast(
            err?.response?.data?.errorMessage ||
              err?.response?.data?.errors ||
              "Error while downloading the report",
            { type: "error", autoClose: 2000 }
          );
        });
    },
  });
  const handleDateTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value) {
      setSelectedDateType(e.target.value);
      const date = new Date();
      switch (e.target.value) {
        case "As of Date":
          formik.setFieldValue("startDate", "");
          break;
        case "Current Financial Year":
          formik.setFieldValue("startDate", `${date.getFullYear()}-04-01`);
          formik.setFieldValue("endDate", `${date.getFullYear() + 1}-03-31`);
          break;
        case "Previous Financial Year":
          formik.setFieldValue("startDate", `${date.getFullYear() - 1}-04-01`);
          formik.setFieldValue("endDate", `${date.getFullYear()}-03-31`);
          break;
        default:
          break;
      }
    }
  };
  const fileName = `Pool Statement_${getCompanyName()}.xlsx`;
  async function downloadReport(reportDto: ReportDto) {
    onClose();
    const blob = await convertBase64ToBlob(
      reportDto.base64File,
      reportDto.fileType
    );
    downloadBlobObject(blob, fileName);
  }

  return (
    <div className="">
      <div className="p-8 text-lg font-medium border-b">
        <h6 className="flex justify-between">
          {data?.headerName}
          <span
            className="cursor-pointer"
            onClick={() => {
              onClose();
            }}
          >
            X
          </span>
        </h6>
      </div>
      <VStack className="w-full gap-2 px-8 py-4 ">
        {existingSchedule && (
          <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-md px-4 py-3 text-sm text-orange-800">
            <Icon
              icon="mdi:clock-alert-outline"
              width={16}
              className="mt-0.5 shrink-0 text-orange-500"
            />
            <span>
              This report is already scheduled (runs every{" "}
              <strong>{existingSchedule.intervalDays}</strong> day
              {existingSchedule.intervalDays !== 1 ? "s" : ""}). Submitting
              again will create an additional schedule.
            </span>
          </div>
        )}
        <HStack className="gap-8 ">
          <div className="flex-1">
            <Label className="text-sm font-normal">Date Type</Label>
            <Select
              options={dateTypes}
              onChange={(e) => handleDateTypeChange(e)}
              value={selectedDateType}
            />
          </div>
          {selectedDateType !== "As of Date" && (
            <div className="flex-1">
              <Label
                className={`text-sm font-normal ${
                  selectedDateType === "As of Date" ? "cursor-not-allowed" : ""
                }`}
              >
                Start Date
              </Label>
              <Input
                className={` ${
                  selectedDateType === "As of Date" ? "cursor-not-allowed" : ""
                }`}
                type="date"
                value={formik.values.startDate}
                onBlur={formik.handleBlur}
                disabled={selectedDateType === "As of Date"}
                onChange={(e) => {
                  if (!e.target.value) return;
                  formik.setFieldValue("startDate", e.target.value);
                }}
              />
            </div>
          )}
          <div className="flex-1">
            <Label className={"text-sm font-normal"}>Till Date</Label>
            <Input
              type="date"
              value={formik.values.endDate}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                if (!e.target.value) return;
                formik.setFieldValue("endDate", e.target.value);
              }}
            />
          </div>
          {selectedDateType === "As of Date" && <div className="flex-1"></div>}
        </HStack>
        <HStack className="gap-8">
          <div className="flex-1">
            <DateFormatSelect value={dateFormat} onChange={setDateFormat} />
          </div>
          <div className="flex-1" />
          <div className="flex-1" />
        </HStack>
        <HStack className="pt-2 items-center gap-2 before:border-b-2 before:border-dashed before:m-auto after:flex-1 after:border-dashed after:content-[''] after:border-b-2">
          <ButtonPrimary1
            onClick={() => {
              if (showAdvancedFilters) {
                formik.setFieldValue("planIds", []);
                formik.setFieldValue("subsidiaryCompanyIds", []);
                formik.setFieldValue("grantStates", []);
              }
              setShowAdvancedFilters(!showAdvancedFilters);
            }}
            className="flex flex-row bg-transparent border border-solid cursor-pointer border-orange-501 hover:bg-transparent"
          >
            Advance Filters
          </ButtonPrimary1>
        </HStack>
        {showAdvancedFilters && (
          <VStack className="w-full gap-2 py-4">
            <HStack className="gap-8">
              <div className="flex-1">
                <Label className={"text-sm font-normal"}>Plan Names</Label>
                <MultiSelectV2
                  _className="w-full"
                  multiple={true}
                  placeholder={"--Select plans--"}
                  options={planOptions}
                  handleSelection={(e) => {
                    const planIds = plans
                      .filter((p) => e.includes(p.planName))
                      .map((p) => p.esopPlanId);
                    formik.setFieldValue("planIds", planIds);
                  }}
                  optionValues={plans
                    .filter((p) =>
                      formik.values.planIds?.includes(p.esopPlanId)
                    )
                    .map((p) => p.planName)}
                />
              </div>
              <div className="flex-1">
                <Label className={"text-sm font-normal"}>
                  Subsidiary Companies
                </Label>
                <MultiSelectV2
                  _className="w-full"
                  multiple={true}
                  placeholder={"--Select subsidiary companies--"}
                  options={subsidiaryNames}
                  handleSelection={(e) => {
                    const subsidiaryCompanyIds = subsidiaryList
                      .filter((s) => e.includes(s.name))
                      .map((s) => s.id);
                    formik.setFieldValue(
                      "subsidiaryCompanyIds",
                      subsidiaryCompanyIds
                    );
                  }}
                  optionValues={subsidiaryList
                    .filter((s) =>
                      (formik.values as any).subsidiaryCompanyIds?.includes(
                        s.id
                      )
                    )
                    .map((s) => s.name)}
                />
              </div>
            </HStack>
            <HStack className="gap-8">
              <div className="flex-1">
                <Label className={"text-sm font-normal"}>Grant States</Label>
                <MultiSelectV2
                  _className="w-full"
                  multiple={true}
                  placeholder={"--Select grant states--"}
                  options={grantStateOptions}
                  handleSelection={(e) => {
                    formik.setFieldValue(
                      "grantStates",
                      e.map((label: string) => grantStateLabels[label])
                    );
                  }}
                  optionValues={(formik.values.grantStates || []).map(
                    getGrantStateLabel
                  )}
                />
              </div>
              <div className="flex-1"></div>
            </HStack>
          </VStack>
        )}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <ScheduleSection
            value={scheduleParams}
            onChange={setScheduleParams}
            users={associatedUsers ?? []}
          />
        </div>
        <HStack className="justify-end gap-2 mt-7">
          <input
            type="checkbox"
            checked={shouldPiiBeMasked}
            onChange={() => setShouldPiiBeMasked(!shouldPiiBeMasked)}
            className="text-white accent-orange-501"
            name=""
            id=""
          />
          <span>Hide all Personally Identifiable Information</span>
        </HStack>
        <HStack className="justify-end ">
          <ButtonPrimary
            onClick={() => {
              if (scheduleParams && scheduleParams.recipients.length === 0) {
                toast(
                  "Please select at least one recipient to schedule the report",
                  { type: "error", autoClose: 2000 }
                );
                return;
              }
              formik.handleSubmit();
            }}
          >
            {scheduleParams && scheduleParams.recipients.length > 0
              ? "Generate & Schedule"
              : "Generate"}
          </ButtonPrimary>
        </HStack>
      </VStack>
    </div>
  );
};

export default PoolStatementDialog;
